import * as React from 'react';
import { cn } from '@/lib/utils';
import { ChevronRight } from 'lucide-react';

export interface MobileListItemProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /**
   * 標題
   */
  title?: React.ReactNode;
  /**
   * 副標題或描述
   */
  subtitle?: React.ReactNode;
  /**
   * 右側內容（如開關、圖標等）
   */
  after?: React.ReactNode;
  /**
   * 是否顯示右側箭頭
   */
  showArrow?: boolean;
  /**
   * 是否顯示底部邊框
   */
  showBorder?: boolean;
  /**
   * 點擊處理函數
   */
  onClick?: () => void;
}

/**
 * iOS 風格列表項
 * 用於顯示列表中的單個項目，支援標題、副標題、右側內容和點擊效果
 */
export const MobileListItem = React.forwardRef<
  HTMLDivElement,
  MobileListItemProps
>(
  (
    {
      className,
      title,
      subtitle,
      after,
      showArrow = false,
      showBorder = true,
      onClick,
      children,
      ...props
    },
    ref
  ) => {
    const isClickable = !!onClick;

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center justify-between px-4 py-3 min-h-[44px]',
          showBorder && 'border-b border-gray-200 last:border-b-0',
          isClickable &&
            'active:bg-gray-100 cursor-pointer transition-colors touch-manipulation',
          className
        )}
        onClick={onClick}
        {...props}
      >
        <div className="flex-1 flex flex-col justify-center min-w-0">
          {title && (
            <div className="text-base text-gray-900 font-medium">{title}</div>
          )}
          {subtitle && (
            <div className="text-sm text-gray-500 mt-0.5">{subtitle}</div>
          )}
          {children}
        </div>
        {(after || showArrow) && (
          <div className="flex items-center gap-2 ml-3 flex-shrink-0">
            {after}
            {showArrow && (
              <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
            )}
          </div>
        )}
      </div>
    );
  }
);
MobileListItem.displayName = 'MobileListItem';
